import { useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { type ListEpmMasterFunctionalitiesQuery, useListEpmMasterFunctionalitiesQuery } from '@/common/graphql';
import { type FunctionalitySuggestion, getFunctionalityTotal, MAX_FUNCTIONALITY_ENTRIES } from '@/common/types';
import { AllocationField } from './AllocationField';
import { AllocationPercentageInput } from './AllocationPercentageInput';
import { AllocationSelectInput } from './AllocationSelectInput';
import type { AllocationFieldWithSuggestionProps, SuggestionAppend } from './props';
import { SuggestionHint } from './SuggestionHint';
import { useGetComboboxData } from './useGetComboboxData';

interface FunctionalitySuggestionAppend extends SuggestionAppend {
  epmMasterFunctionalityId: string;
}

export const AllocationFieldFunctionality = ({
  control,
  errors,
  fields,
  append,
  remove,
  watched,
  suggestions,
  showSuggestionHint,
  register,
}: AllocationFieldWithSuggestionProps<'functionality', FunctionalitySuggestion, FunctionalitySuggestionAppend>) => {
  const total = useMemo(() => getFunctionalityTotal(watched), [watched]);
  const { options, loading } = useGetComboboxData<ListEpmMasterFunctionalitiesQuery>(
    useListEpmMasterFunctionalitiesQuery,
    'listEpmMasterFunctionalities',
  );

  return (
    <AllocationField
      addLabel="Add Functionality"
      errors={errors}
      fields={fields}
      formKey="functionality"
      loading={loading}
      maxEntries={MAX_FUNCTIONALITY_ENTRIES}
      title="Functionality"
      total={total}
      onAdd={() => append({ epmMasterFunctionalityId: '', percentage: 0 })}
      onRemove={remove}
      suggestionHint={
        <SuggestionHint
          show={showSuggestionHint}
          suggestions={suggestions}
          watched={watched}
          isItemDifferent={(w, s) => w.epmMasterFunctionalityId !== s.epmMasterFunctionalityId}
          renderName={s => s.name}
        />
      }
      renderRowContent={i => {
        const alreadySelected = watched.slice(0, i).map(it => it.epmMasterFunctionalityId);
        return (
          <>
            <Controller
              control={control}
              name={`functionality.${i}.epmMasterFunctionalityId`}
              render={({ field }) => (
                <AllocationSelectInput
                  autoSelectSingle={true}
                  data={options.filter(it => !alreadySelected.includes(it.value))}
                  disabled={loading}
                  placeholder="Functionality"
                  value={field.value || null}
                  onChange={val => field.onChange(val ?? '')}
                />
              )}
            />
            <AllocationPercentageInput
              registration={register(`functionality.${i}.percentage`, { valueAsNumber: true })}
              disabled={loading}
            />
          </>
        );
      }}
    />
  );
};
